import {
  type ActionEventArgs,
  type EventClickArgs,
  type EventRenderedArgs,
  type PopupOpenEventArgs,
  type ScheduleComponent,
} from '@syncfusion/ej2-react-schedule'
import dayjs from 'dayjs'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import type { AppointmentCategories } from 'features/Schedule/api/schedule.types'
import type { AppointmentColorType } from 'features/SettingsOld/interfaces/ISettings'
import type { StatusColorName } from 'features/SettingsOld/state/api/types'
import { type MutableRefObject, type RefObject, useCallback, useEffect, useMemo, useRef } from 'react'
import { type AppointmentColors, applyAppointmentColors } from './applyAppointmentColors'
import CalendarEvent from './CalendarEvent'
import {
  applyAppointmentFill,
  applyScheduleColumnWidths,
  getAppointmentColor,
  getAppointmentColorInputFromEvent,
  getMaxConcurrentByColumnKey,
} from './helpers'
import { revertScheduleDomChange } from './safeRefreshScheduleEvents'
import { scheduleEventStore } from './scheduleEventStore'
import { useScheduleColumnResize } from './useScheduleColumnResize'
import { useSilentEventSync } from './useSilentEventSync'

type ScheduleEventData = IPatientSchedule & {
  status?: { code?: number }
  category?: { code?: number }
}

type UseScheduleViewParams = {
  data: IPatientSchedule[]
  schedules: IPatientSchedule[]
  scheduleObj: RefObject<ScheduleComponent | null>
  interval: number
  dayInterval: { min: string; max: string }
  day?: string
  timeZone?: string
  appointmentColor: AppointmentColorType
  appointmentCategories: AppointmentCategories[]
  patientStatuses: StatusColorName[]
  hasEhrBooking: boolean
  showConsultationStaffInitials: boolean
  isWeekView?: boolean
  columnKeys?: string[]
  /** Waiting-room column key; absorbs leftover day-view width when other rooms are not resized. */
  flexColumnKey?: string
  /** Per-column unit widths at default-zoom reference scale (user localStorage prefs). */
  referenceWidthByColumn?: Readonly<Record<string, number>>
  setColumnReferenceWidth?: (columnKey: string, referenceWidthPx: number) => void
  setEditedPatient: (patient: IPatientSchedule | null) => void
  prepareScheduleChange: (data: IPatientSchedule) => unknown | null
  commitScheduleChange: (prepared: any, event: IPatientSchedule) => Promise<void>
  skipNextEventChangeRef?: MutableRefObject<boolean>
  instanceKey?: string
}

const findEventById = (
  events: IPatientSchedule[],
  id: number | string | undefined,
): IPatientSchedule | undefined => {
  if (id == null) return undefined
  return events.find((event) => event.id === id || Number(event.id) === Number(id))
}

const toLocalCalendarDate = (dateKey?: string): Date => {
  if (!dateKey) return new Date()
  const [year, month, dayOfMonth] = dateKey.split('-').map(Number)
  if (!year || !month || !dayOfMonth) return new Date(dateKey)
  return new Date(year, month - 1, dayOfMonth, 0, 0, 0, 0)
}

/**
 * Shared Syncfusion scheduler wiring used by both the day and week calendars:
 * appointment colouring, the event template, and the common event handlers/config.
 * View-specific concerns (resources, render-cell rules, selection) stay in each view.
 */
export const useScheduleView = ({
  data,
  schedules,
  scheduleObj,
  interval,
  dayInterval,
  day,
  timeZone,
  appointmentColor,
  appointmentCategories,
  patientStatuses,
  hasEhrBooking,
  showConsultationStaffInitials,
  isWeekView = false,
  columnKeys = [],
  flexColumnKey,
  referenceWidthByColumn = {},
  setColumnReferenceWidth,
  setEditedPatient,
  prepareScheduleChange,
  commitScheduleChange,
  skipNextEventChangeRef,
  instanceKey,
}: UseScheduleViewParams) => {
  const dataRef = useRef(data)
  const schedulesRef = useRef(schedules)
  const columnKeysRef = useRef(columnKeys)
  const flexColumnKeyRef = useRef(flexColumnKey)
  const referenceWidthByColumnRef = useRef(referenceWidthByColumn)
  const scheduleInstanceKey = instanceKey ?? `${isWeekView ? 'week' : 'day'}:${day ?? ''}`

  useEffect(() => {
    dataRef.current = data
  }, [data])

  useEffect(() => {
    schedulesRef.current = schedules
  }, [schedules])

  useEffect(() => {
    columnKeysRef.current = columnKeys
  }, [columnKeys])

  useEffect(() => {
    flexColumnKeyRef.current = flexColumnKey
  }, [flexColumnKey])

  useEffect(() => {
    referenceWidthByColumnRef.current = referenceWidthByColumn
  }, [referenceWidthByColumn])

  const handleColumnResizeEnd = useCallback(
    (columnKey: string, referenceWidthPx: number) => {
      setColumnReferenceWidth?.(columnKey, referenceWidthPx)
    },
    [setColumnReferenceWidth],
  )

  const { reattachColumnResizeHandles } = useScheduleColumnResize({
    scheduleObj,
    onResizeEnd: handleColumnResizeEnd,
  })

  const applyDynamicColumnWidths = useCallback(() => {
    const schedule = scheduleObj.current
    if (!schedule?.element) return

    const root = schedule as unknown as { isDestroyed?: boolean }
    if (root.isDestroyed) return

    let keys = columnKeysRef.current
    if (isWeekView) {
      keys = schedule.getCurrentViewDates().map((date) => String(dayjs(date).day()))
    }

    const maxConcurrentByColumn = getMaxConcurrentByColumnKey(dataRef.current)
    const fillSingleDayColumn = !isWeekView && keys.length === 1

    if (keys.length > 0) {
      applyScheduleColumnWidths({
        scheduleElement: schedule.element,
        columnKeys: keys,
        maxConcurrentByColumn,
        interval,
        referenceWidthByColumn: referenceWidthByColumnRef.current,
        fillContainer: fillSingleDayColumn,
        flexColumnKey: isWeekView ? undefined : flexColumnKeyRef.current,
      })
    }

    applyAppointmentFill(schedule.element)
    reattachColumnResizeHandles({
      columnKeys: fillSingleDayColumn ? [] : keys,
      interval,
      maxConcurrentByColumn,
      isWeekView,
    })
  }, [scheduleObj, isWeekView, interval, reattachColumnResizeHandles])

  const getEventColors = useCallback(
    (eventData: IPatientSchedule): AppointmentColors => {
      const live =
        findEventById(dataRef.current, eventData.id) ??
        findEventById(schedulesRef.current, eventData.id) ??
        eventData

      const { statusCode, categoryCode } = getAppointmentColorInputFromEvent(
        live,
        schedulesRef.current,
      )
      return getAppointmentColor({
        appointmentColor,
        categoryStatuses: appointmentCategories,
        statusColorName: patientStatuses,
        statusCode,
        categoryCode,
      })
    },
    [appointmentColor, appointmentCategories, patientStatuses],
  )

  const getEventColorsRef = useRef(getEventColors)
  getEventColorsRef.current = getEventColors

  const reapplyAppointmentColors = useCallback(() => {
    const schedule = scheduleObj.current
    if (!schedule?.element) return

    const root = schedule as unknown as { isDestroyed?: boolean }
    if (root.isDestroyed) return

    for (const event of dataRef.current) {
      const colors = getEventColorsRef.current(event)
      schedule.element
        .querySelectorAll<HTMLElement>(
          `[data-id="Appointment_${event.id}"]:not(.e-schedule-event-clone)`,
        )
        .forEach((element) => applyAppointmentColors(element, colors))
    }
  }, [scheduleObj])

  const eventRendered = useCallback((props: EventRenderedArgs) => {
    applyAppointmentColors(props.element, getEventColorsRef.current(props.data as IPatientSchedule))
  }, [])

  const applyDynamicColumnWidthsRef = useRef(applyDynamicColumnWidths)
  applyDynamicColumnWidthsRef.current = applyDynamicColumnWidths

  const dataSource = useSilentEventSync({
    scheduleObj,
    data,
    getEventColors,
    timeZone,
    instanceKey: scheduleInstanceKey,
    onPatched: () => applyDynamicColumnWidthsRef.current(),
  })

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      applyDynamicColumnWidths()
    })
    return () => cancelAnimationFrame(frameId)
  }, [columnKeys, referenceWidthByColumn, applyDynamicColumnWidths])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 576px)')
    const handleBreakpointChange = () => applyDynamicColumnWidths()
    mediaQuery.addEventListener('change', handleBreakpointChange)
    window.addEventListener('resize', handleBreakpointChange)
    return () => {
      mediaQuery.removeEventListener('change', handleBreakpointChange)
      window.removeEventListener('resize', handleBreakpointChange)
    }
  }, [applyDynamicColumnWidths])

  const eventTemplate = useCallback(
    (props: ScheduleEventData) => (
      <CalendarEvent
        event={props}
        appointmentColor={appointmentColor}
        appointmentCategories={appointmentCategories}
        patientStatuses={patientStatuses}
        isWeekView={isWeekView}
        hasEhrBooking={hasEhrBooking}
        showConsultationStaffInitials={showConsultationStaffInitials}
        timeZone={timeZone}
        instanceKey={scheduleInstanceKey}
      />
    ),
    [
      appointmentColor,
      appointmentCategories,
      hasEhrBooking,
      scheduleInstanceKey,
      isWeekView,
      patientStatuses,
      showConsultationStaffInitials,
      timeZone,
    ],
  )

  const handleEventClick = useCallback(
    (props: EventClickArgs) => {
      const originalEvent = (props as { originalEvent?: Event }).originalEvent
      originalEvent?.preventDefault()
      originalEvent?.stopPropagation()
      setEditedPatient(
        (scheduleEventStore.get(
          Number((props.event as IPatientSchedule).id),
          scheduleInstanceKey,
        ) as IPatientSchedule | undefined) ?? (props.event as IPatientSchedule),
      )
    },
    [setEditedPatient, scheduleInstanceKey],
  )

  const handleRemoveSelectedCells = useCallback(() => {
    document.querySelectorAll('.e-selected-cell')?.forEach((el) => {
      el.classList.remove('e-selected-cell')
    })
  }, [])

  const handleChange = useCallback(
    async (props: ActionEventArgs) => {
      if (props.requestType !== 'eventChange') {
        return
      }

      if (skipNextEventChangeRef?.current) {
        skipNextEventChangeRef.current = false
        return
      }

      const changedEvent = props.data as IPatientSchedule

      const prepared = prepareScheduleChange(changedEvent)

      if (!prepared) {
        props.cancel = true
        handleRemoveSelectedCells()
        requestAnimationFrame(() => {
          revertScheduleDomChange(scheduleObj.current)
          reapplyAppointmentColors()
        })
        return
      }

      commitScheduleChange(prepared, changedEvent).finally(() => {
        handleRemoveSelectedCells()
        requestAnimationFrame(() => reapplyAppointmentColors())
      })
    },
    [
      prepareScheduleChange,
      commitScheduleChange,
      handleRemoveSelectedCells,
      reapplyAppointmentColors,
      scheduleObj,
      skipNextEventChangeRef,
    ],
  )

  const handleDataBound = useCallback(() => {
    scheduleObj.current?.hideSpinner()
    reapplyAppointmentColors()
    applyDynamicColumnWidths()
  }, [scheduleObj, reapplyAppointmentColors, applyDynamicColumnWidths])

  const handleActionFailure = useCallback(() => {
    scheduleObj.current?.hideSpinner()
  }, [scheduleObj])

  const handlePopupOpen = useCallback((props: PopupOpenEventArgs) => {
    if (props.type === 'Editor') {
      props.cancel = true
    }
  }, [])

  const majorSlotTemplate = useCallback(
    (props: { date: Date }) => (
      <div className='e-time-slot-wrap'>{dayjs(props.date).format('h:mm A')}</div>
    ),
    [],
  )

  const selectedDate = useMemo(() => {
    return toLocalCalendarDate(day)
  }, [day])

  const timeScale = useMemo(
    () => ({ interval, slotCount: 1, majorSlotTemplate }),
    [interval, majorSlotTemplate],
  )

  const workHours = useMemo(
    () => ({ highlight: true, start: dayInterval.min, end: dayInterval.max }),
    [dayInterval.min, dayInterval.max],
  )

  const eventSettings = useMemo(
    () => ({
      dataSource,
      enableTooltip: false,
      enableIndicator: true,
      fields: {
        id: 'id',
        startTime: { title: 'From', name: 'start_time' },
        endTime: { title: 'To', name: 'end_time' },
      },
    }),
    [dataSource],
  )

  return {
    eventRendered,
    eventTemplate,
    handleEventClick,
    handleChange,
    handleDataBound,
    handleActionFailure,
    handlePopupOpen,
    handleRemoveSelectedCells,
    reapplyAppointmentColors,
    applyDynamicColumnWidths,
    majorSlotTemplate,
    selectedDate,
    timeScale,
    workHours,
    eventSettings,
  }
}
